import { useState } from "react";
import { Checkbox } from "@volca/ui/components/ui/checkbox";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { GuideChecklist } from "./guides";
import { useOnboardingState } from "./onboarding-state-context";
import {
  PERMISSION_PRODUCT,
  permissionGroupsFor,
  type PermissionProduct,
} from "./st-permissions";

const storageKey = (id: string) => `volca.guide-checklist.${id}`;

/**
 * Reading and writing are both allowed to fail. Private windows, blocked site data and
 * storage quotas all throw, and a guide whose ticks do not stick is still a usable
 * guide, so nothing here is allowed to take the panel down with it.
 */
const readTicks = (id: string): string[] => {
  try {
    const raw = window.localStorage.getItem(storageKey(id));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed)
      ? parsed.filter((value) => typeof value === "string")
      : [];
  } catch {
    return [];
  }
};

const writeTicks = (id: string, ticks: string[]) => {
  try {
    window.localStorage.setItem(storageKey(id), JSON.stringify(ticks));
  } catch {
    // Nothing to do. The ticks still work for this sitting.
  }
};

/**
 * A guide's checklist, ticked off as the customer works through it.
 *
 * The ticks are theirs alone. Nothing is sent to us and nothing is read back: this is a
 * place to keep your finger while you work through somebody else's settings screen, not
 * a report on whether the work was done. What the step actually needs is the connection
 * working, which we check ourselves.
 */
export const GuideChecklistView = ({
  checklist,
}: {
  checklist: GuideChecklist;
}) => {
  const state = useOnboardingState();
  const [ticks, setTicks] = useState<string[]>(() => readTicks(checklist.id));

  // What this business runs decides the list. The two permission sheets overlap heavily,
  // so a combined business gets the union with each permission named once.
  const products: PermissionProduct[] = [
    ...(state?.steps.includes("schedule") ? [PERMISSION_PRODUCT.payroll] : []),
    ...(state?.steps.includes("invoice_sources")
      ? [PERMISSION_PRODUCT.accountsPayable]
      : []),
  ];
  const groups = permissionGroupsFor(products);
  const all = groups.flatMap((group) => group.items);
  const done = all.filter((item) => ticks.includes(item.label)).length;
  const anyHidden = all.some((item) => item.hidden);

  // Updated from the previous value rather than from the render's, because several
  // boxes can be ticked before React renders again and a closure over `ticks` would
  // keep only the last one. The write rides along, so storage never disagrees with the
  // state; running it twice under StrictMode writes the same value.
  const toggle = (label: string, checked: boolean) =>
    setTicks((current) => {
      const next = checked
        ? current.concat(current.includes(label) ? [] : [label])
        : current.filter((tick) => tick !== label);
      writeTicks(checklist.id, next);
      return next;
    });

  if (!all.length) return null;

  return (
    <Stack gap={4} align="stretch" className="mt-4 w-full">
      <Stack
        direction="row"
        justify="between"
        align="baseline"
        gap={2}
        className="w-full border-b border-line pb-2"
      >
        <Text variant="label">Permissions</Text>
        <Text variant="caption" tone="muted">
          {done} of {all.length}
        </Text>
      </Stack>

      {groups.map((group) => (
        <Stack className="w-full" key={group.title} gap={1} align="stretch">
          <Text
            weight="semibold"
            variant="caption"
            tone="muted"
            className="tracking-wider uppercase"
          >
            {group.title}
          </Text>
          {group.items.map((item) => {
            const id = `${checklist.id}-${item.label}`;
            return (
              <Stack
                className="w-full"
                key={item.label}
                direction="row"
                gap={2}
                align="start"
              >
                <Checkbox
                  id={id}
                  className="mt-0.5"
                  checked={ticks.includes(item.label)}
                  onCheckedChange={(checked) =>
                    toggle(item.label, checked === true)
                  }
                />
                <Label htmlFor={id} className="leading-snug font-normal">
                  {item.label}
                  {item.hidden && (
                    <span className="text-subtle" aria-hidden="true">
                      {" *"}
                    </span>
                  )}
                </Label>
              </Stack>
            );
          })}
        </Stack>
      ))}

      {anyHidden && (
        <Text variant="caption" tone="muted">
          * ServiceTitan only shows these once purchasing is turned on for your
          account. If one is missing, their support can enable it.
        </Text>
      )}

      <Text variant="caption" tone="muted">
        Just for you. These stay in your browser.
      </Text>
    </Stack>
  );
};
